/* eslint-disable quote-props */
import {batchActions} from 'redux-batched-actions';
import getPath from '@neonaut/lib-js/es/object/getPath';

import {async, set} from '@mapsight/core/lib/base/actions';
import {updateFeatureProperty} from '@mapsight/core/lib/feature-sources/actions';
import {FEATURE_SOURCES} from '@mapsight/ui/src/js/config/constants/controllers';

import {isRecord, retryExponential} from '../../modules/helpers';

import {RATINGS, SendingState} from './controller';


export const RATINGS_GET_RESULT = 'RATINGS_GET_RESULT';
export const RATINGS_SEND = 'RATINGS_SEND';
export const RATINGS_SEND_RESULT = 'RATINGS_CREATE_RESULT';

const API_WRONG_CAPTCHA = 'wrongCaptcha';


/**
 * @param {Response} response server's response
 * @returns object
 * */
async function handleJsonResponse(response) {
	const jsonData = await response.json();
	if (!isRecord(jsonData)) {
		throw new Error('Error: broken response data structure');
	}

	if ('err' in jsonData) {
		if (isRecord(jsonData.err)) {
			if (typeof jsonData.err.messageCode === 'string') {
				throw new Error( jsonData.err.messageCode === API_WRONG_CAPTCHA ?
						API_WRONG_CAPTCHA :
						`Miscellaneous error (unknown message code: ${jsonData.err.messageCode})`,
				);
			}
			if (typeof jsonData.err.message === 'string' && jsonData.err.message !== '') {
				throw new Error(
					`Miscellaneous error (unknown message code: ${jsonData.err.messageCode})`
				);
			}
		}
		throw new Error(
			`Unkown error in response`
		);
	}

	return jsonData;
}


function getCaptchaResult(question) {
	return set([RATINGS, 'captcha'], {question});
}

function getCaptchaError() {
	return set([RATINGS, 'captcha'], {error: true});
}

export const getCaptcha = (apiEndpoint) => async(function handleGetCaptcha(dispatch, getGlobalState) {
	return (
		retryExponential(async () => {
			const res = await fetch(`${apiEndpoint}/get-captcha`, {
				// just use POST for 'anti'-caching etc...
				method: 'POST',
				headers: {
					'Accept': 'application/json; charset=UTF-8',
				},
				// enable cookie handling
				credentials: 'include',
			});
			const resData = await handleJsonResponse(res);
			if (!isRecord(resData.ok) || typeof resData.ok.captcha !== 'string') {
				throw new Error('Missing captcha in server response');
			}

			return resData.ok.captcha;
		})
			.then((question) => {
				if (question) {
					dispatch(getCaptchaResult(question));
				}
			})
			.catch((err) => {
				console.error('Error loading captcha', err);
				dispatch(getCaptchaError());
			})
	);
});

const isValidRatingsProperty = ratings => (
	isRecord(ratings)
	&& ratings.ratings
	&& Array.isArray(ratings.ratings)
);

const updateRatingsProperty = (sourceId, poiId, ratings) => updateFeatureProperty(
	FEATURE_SOURCES,
	sourceId,
	poiId,
	'ratings',
	ratings // das äußere ratings aus dem JSON entspricht der Property
);

const getResult = (sourceId, poiId, result) => ({
	type: RATINGS_GET_RESULT,
	sourceId,
	poiId,
	result
});

// get wird als Operation ausgeführt, die beliebig aufgerufen werden kann
// es wird immer das updatedAt vom Result mit den vorhandenen Daten verglichen und nur übernommen, wenn die ankommenden Daten neuer sind und die gespeicherten Daten bereits erfolgreich gesendet sind
// Fehlerfall wird irgnoriert, weil diese Daten nicht so wichtig sind
export const get = (apiEndpoint, authorToken, sourceId, poiId) => async(function handleGet(dispatch, getGlobalState) {
	return (retryExponential(async () => {
		const res = await fetch(`${apiEndpoint}/get`, {
			// just use POST for 'anti'-caching etc...
			method: 'POST',
			body: JSON.stringify({token: authorToken}),
			headers: {
				'Content-Type': 'application/json; charset=UTF-8',
				'Accept': 'application/json; charset=UTF-8',
			},
			// enable cookie handling
			credentials: 'include',
		});
		const jsonData = await handleJsonResponse(res);
		if (!isRecord(jsonData?.ok)) {
			throw new Error('missing record "ok" in jsonData');
		}
		return jsonData.ok;
	}).then(({thisRating, ratings}) => {
		const actions = [];
		const usersRating = thisRating?.status === 'ok' && thisRating?.rating;
		if (
			isRecord(usersRating)
			&& typeof usersRating.id === 'number'
			&& typeof usersRating.rating === 'number'
			&& typeof usersRating.author === 'string'
			&& (usersRating.lang === 'en' || usersRating.lang === 'de')
			&& typeof usersRating.comment === 'string'
			&& typeof usersRating.createDate === 'string'
		) {
			actions.push(getResult(sourceId, poiId, usersRating));
		}
		if (
			isValidRatingsProperty(ratings)
		) {
			try {
				actions.push(updateRatingsProperty(
					sourceId,
					poiId,
					ratings
				));
			} catch (err) {
				console.warn('ignored error while updating ratings data', err);
			}
		}
		if (actions.length) {
			dispatch(batchActions(actions));
		}
	}).catch(err => {
		console.warn('ignoring error on rating/get', err);
	}));
});

const send = (sendRequestId, sourceId, poiId, rating, lang, comment, author) => ({
	type: RATINGS_SEND,
	sendRequestId,
	sourceId, poiId, rating, lang, comment, author
});

const sendResult = (sendRequestId, sourceId, poiId, state, token) => ({
	type: RATINGS_SEND_RESULT,
	sendRequestId,
	sourceId, poiId,
	state, token
});

export const putRating = (apiEndpoint, captchaAnswer, sourceId, poiId, rating, lang, comment, author, authorToken) =>
	async(function handleEdit(dispatch, getGlobalState) {
		const lastRequestId = getPath(getGlobalState(), [RATINGS, 'sendRequestIds', sourceId, poiId], 0);
		const thisRequestId = lastRequestId + 1;

		console.log('async create-or-edit', {getState: getGlobalState(), lastRequestId, thisRequestId});

		dispatch(send(thisRequestId, sourceId, poiId, rating, lang, comment, author)); // TODO entspricht setSendState({type: 'sending'}); und sichert Daten im Browser-Storage

		return (
			(async () => {
				const action = 'create-or-edit';
				const res = await fetch(
					`${apiEndpoint}/${action}`,
					{
						method: 'POST',
						body: JSON.stringify({
							rating,
							author,
							comment,
							captcha: captchaAnswer,
							token: authorToken || undefined,
						}),
						headers: {
							'Content-Type': 'application/json; charset=UTF-8',
							'Accept': 'application/json; charset=UTF-8',
						},
						// enable cookie handling
						credentials: 'include',
					},
				);

				const jsonData = await handleJsonResponse(res);
				if (!isRecord(jsonData?.ok)) {
					throw new Error('missing record "ok" in jsonData');
				}
				return jsonData.ok;
			})()
				.then(({token, ratings}) => {
					console.log('rating sent succsesfully', {token, ratings, thisRequestId, sourceId, poiId});

					const actions = [];
					if(typeof token === 'string') {
						actions.push(sendResult(
							thisRequestId,
							sourceId,
							poiId,
							SendingState.SENT,
							token
						));
					}
					if (
						isValidRatingsProperty(ratings)
					) {
						try {
							actions.push(updateRatingsProperty(
								sourceId,
								poiId,
								ratings
							));
						} catch (err) {
							console.warn('ignored error while updating ratings data', err);
						}
					}
					if (actions.length) {
						dispatch(batchActions(actions));
					}
				})
				.catch((err) => {
					console.error('error while sending', err, {thisRequestId, sourceId, poiId});
					dispatch(sendResult(
						thisRequestId,
						sourceId,
						poiId,
						err.message === API_WRONG_CAPTCHA ? SendingState.WRONG_CAPTCHA : SendingState.FAILED
					));
				})
				.finally(() => {
					dispatch(getCaptcha(apiEndpoint));
				})
		);
	});
